var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Axios from 'axios';
import { AbortError } from '../../_internal/error/AbortError';
import { HttpError } from '../../_internal/error/HttpError';
import { emitEvent } from '../events/module';
/**
 * Enum containing the available HTTP methods
 */
var Method;
(function (Method) {
    Method["GET"] = "GET";
    Method["POST"] = "POST";
    Method["PUT"] = "PUT";
    Method["PATCH"] = "PATCH";
    Method["DELETE"] = "DELETE";
    Method["OPTIONS"] = "OPTIONS";
})(Method || (Method = {}));
/**
 * Upload a file through FormData or Blob
 */
export function httpUpload(config) {
    return _makeRequest(Object.assign({ method: Method.POST }, config));
}
/**
 * Upload a file to S3
 *
 * Uses PUT instead of POST, like with regular file upload.
 */
export function httpUploadS3(config) {
    return _makeRequest(Object.assign({ method: Method.PUT }, config));
}
/**
 * Make POST request
 */
export function httpPost(config) {
    return _makeRequest(Object.assign({ method: Method.POST }, config));
}
/**
 * Make GET request
 */
export function httpGet(config) {
    return _makeRequest(Object.assign({ method: Method.GET }, config));
}
export function fetchJson(config) {
    return _makeRequest(Object.assign({ method: Method.GET, headers: {
            'Content-Type': null,
        }, responseType: 'json' }, config));
}
/**
 * Make PUT request
 */
export function httpPut(config) {
    return _makeRequest(Object.assign({ method: Method.PUT }, config));
}
/**
 * Make PATCH request
 */
export function httpPatch(config) {
    return _makeRequest(Object.assign({ method: Method.PATCH }, config));
}
/**
 * Make DELETE request
 */
export function httpDelete(config) {
    return _makeRequest(Object.assign({ method: Method.DELETE }, config));
}
/**
 * Make OPTIONS request
 */
export function options(config) {
    return _makeRequest(Object.assign({ method: Method.OPTIONS }, config));
}
function jsonToUrlEncoded(element, key = '', usp = new URLSearchParams()) {
    if (element && typeof element === 'object') {
        for (const idx in element) {
            jsonToUrlEncoded(element[idx], key ? `${key}[${idx}]` : idx, usp);
        }
    }
    else {
        usp.set(key, element);
    }
    return usp;
}
/**
 * Ensures request config is in correct format and sets default values.
 */
function _formatRequestConfig(config) {
    var _a;
    config = Object.assign(Object.assign({}, config), { 
        // Set default headers
        headers: Object.assign({ 
            // Default to application/json
            'Content-Type': 'application/json' }, config.headers) });
    // Delete headers that have been set to `null`. This prevents them from being sent to the server as empty.
    if (config.headers) {
        for (const key in config.headers) {
            if (Object.hasOwn(config.headers, key)) {
                const element = config.headers[key];
                if (element === null) {
                    delete config.headers[key];
                }
            }
        }
    }
    if (config.method) {
        // Ensure method is lowercase
        config.method = config.method.toLowerCase();
    }
    // Ensure data is properly encoded
    if (typeof config.data === 'object' &&
        !(config.data instanceof FormData || config.data instanceof Blob || config.data instanceof File)) {
        if (config.data && ((_a = config.headers) === null || _a === void 0 ? void 0 : _a['Content-Type']) === 'application/x-www-form-urlencoded') {
            // Convert to params object not required. Axios will convert it for us.
            config.data = jsonToUrlEncoded(config.data);
        }
        else {
            // Stringify data
            config.data = JSON.stringify(config.data);
        }
    }
    return config;
}
/**
 * Use Axios to make request.
 *
 * Formats errors into API standard.
 *
 * @param config Request config
 */
export function _makeRequest(config) {
    return __awaiter(this, void 0, void 0, function* () {
        config = _formatRequestConfig(config);
        const source = Axios.CancelToken.source();
        const abortControllerSignal = config.abortController ? config.abortController.signal : config.signal;
        if (abortControllerSignal) {
            // Listen for 'abort' on the AbortController and pass it on to Axios' abort method
            abortControllerSignal.addEventListener('abort', () => {
                source.cancel();
            });
        }
        return Axios.request(Object.assign(Object.assign({}, config), { headers: config.headers, cancelToken: source.token, transformRequest: (data, headers) => {
                if (headers) {
                    // Clear Axios defaults. We don't need them.
                    headers.common = false;
                    headers.patch = false;
                    headers.post = false;
                    headers.put = false;
                }
                return data;
            } }))
            .catch((e) => {
            var _a;
            if (abortControllerSignal === null || abortControllerSignal === void 0 ? void 0 : abortControllerSignal.aborted) {
                throw new AbortError('Network request aborted.');
            }
            const httpError = new HttpError(e);
            // Improve error object for logging
            const logError = Object.assign({}, httpError);
            logError.axiosError.stack = undefined;
            logError.stack = undefined;
            // biome-ignore lint/suspicious/noExplicitAny: let it go
            logError.responseData = (_a = logError.axiosError.response) === null || _a === void 0 ? void 0 : _a.data;
            emitEvent('http', 'error', httpError);
            throw httpError;
        })
            .then((res) => {
            // L.debug(res);
            return res;
        });
    });
}
